import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { withFormikField } from 'utils/formik';

import { Checkbox, Flexer } from '@ontruck/orc';

import { ExtraInfoSection } from '../../../sections';

import { StyledShipmentBox, StyledShipmentTitle } from './styled';

import { AddressSelector, Calendar, Hour } from 'modules/shipper-order/Form/fields';

import RemoveStopButton from './RemoveStopButton';
import ReorderStopButtons from './ReorderStopButtons';
import StyledAppointmentContainer from './styled/StyledAppointmentContainer';
import Locator from '../Locator';
import {
  LOCATOR_DROPOFF_FIELD,
  LOCATOR_PICKUP_FIELD,
} from 'modules/shipper-order/Form/Intraser/tenantConstants';
import { injectIntl } from 'react-intl';
import LoadWeight from '../LoadWeight';

const StyledDiv = styled.div`
  display: flex;
  width: 50%;
  > div {
    flex-grow: 1;
    > div > div {
      width: 100%;
      > div {
        width: 100%;
        > div {
          width: 100%;
          > div  {
            width: 100%;
            > div > div {
              width: 100%;
            }
          }
        }
      }
    }
  }

  .locator {
    width: 10%;
  }
`;

const StopSection = ({
  stopPreffix,
  addressSuffix,
  allowRemove,
  onRemove,
  onNewAddress,
  shipperAddresses,
  userInfo,
  isMainStop,
  showExtraInfo,
  isEdition,
  shipment,
  locator,

  allowMoveUp,
  allowMoveDown,
  onMoveUp,
  onMoveDown,

  field,
  form,
  disabledFields,
  onTimeChange,
  isRepeated,
  intl,
  key,
}) => {
  const t = intl.messages.modules.shipper_order.form.stop_section;
  const { values, setFieldValue } = form;
  const { name, value } = field;
  const locatorField = stopPreffix === 'dropoff' ? LOCATOR_DROPOFF_FIELD : LOCATOR_PICKUP_FIELD;
  const {
    has_dropoff_appointment: hasDropoffAppointment,
    dropoff_appointment_required: dropoffAppointmentRequired,
  } = shipment;

  if (!value) {
    return null;
  }

  const { shipments = [] } = values;

  const isDisabledByDropoffAppointment = dropoffAppointmentRequired && !hasDropoffAppointment;
  const handleHasAppointmentChange = (checked) => {
    const hasAppointmentName = [...name.split('.'), 'has_dropoff_appointment'];
    setFieldValue(hasAppointmentName, checked);
  };

  // This renders the dates and times inputs for the shipment or appointment
  const renderTimings = (isForAppointment) => {
    const nameFrom = isForAppointment
      ? [name, 'dropoff_appointment', 'time_from']
      : [name, `${stopPreffix}_time_from`];
    const nameTo = isForAppointment
      ? [name, 'dropoff_appointment', 'time_to']
      : [name, `${stopPreffix}_time_to`];

    const firstPickup = isForAppointment ? null : shipments[0]?.pickup_time_from;
    return (
      <Flexer.Parent
        flexDirection="row"
        data-testid={
          isForAppointment ? 'stop-section__appointment-datetimes' : 'stop-section__datetimes'
        }
      >
        <Calendar
          labelDay={t.placeholder_day_from}
          firstPickup={firstPickup}
          name={nameFrom}
          onTimeChange={onTimeChange}
          isRepeated={isRepeated}
          disabled={isDisabledByDropoffAppointment}
        />
        <Hour
          labelHour={t.placeholder_time_from}
          name={nameFrom}
          onTimeChange={onTimeChange}
          disabled={isDisabledByDropoffAppointment}
        />
        <Hour
          labelHour={t.placeholder_time_to}
          name={nameTo}
          onTimeChange={onTimeChange}
          disabled={isDisabledByDropoffAppointment}
        />
      </Flexer.Parent>
    );
  };

  return (
    <StyledShipmentBox mBottom="M" data-testid="stop-section">
      <StyledShipmentTitle>{t.titles[addressSuffix]}</StyledShipmentTitle>
      <Flexer.Parent alignItems="center">
        <StyledDiv>
          <Flexer.Child
            flexGrow="0"
            mRight="S"
            data-e2e={`shipperOrder_edit__address_${addressSuffix}`}
          >
            <AddressSelector
              name={[name, `address_${addressSuffix}`]}
              placeholder={t.address_placeholder[addressSuffix]}
              addresses={shipperAddresses}
              onSaveAddress={onNewAddress}
              zipcodeRE={userInfo.zipcodeRE}
              country={userInfo.country}
              onChange={(address) => {
                isMainStop &&
                  setFieldValue(
                    'shipments',
                    shipments.map((s) => ({
                      ...s,
                      [`address_${addressSuffix}`]: address,
                    }))
                  );
              }}
              disabled={disabledFields.includes('address')}
            />
          </Flexer.Child>
          {locator && (
            <Flexer.Child mRight="XS" className="locator">
              <Locator
                name={[`shipments[0].loads[0]`, locatorField]}
                onChange={(e, { value }) => {
                  setFieldValue(
                    'shipments',
                    shipments.map((s, i) =>
                      i === 0
                        ? {
                            ...s,
                            loads: [
                              {
                                ...s.loads[0],
                                [locatorField]: value,
                              },
                            ],
                          }
                        : { ...s }
                    )
                  );
                }}
              />
            </Flexer.Child>
          )}
          {!locator && (
            <Flexer.Child mRight="XS">
              <LoadWeight name={[`${name}.loads[0]`, 'total_weight']} />
            </Flexer.Child>
          )}
        </StyledDiv>
        <StyledDiv>
          <Flexer.Child>
            {isEdition && dropoffAppointmentRequired && stopPreffix === 'dropoff' && (
              <StyledAppointmentContainer>
                <Checkbox
                  checked={hasDropoffAppointment}
                  onChange={(event, { value: checked }) => handleHasAppointmentChange(checked)}
                  label={t.set_up_appointment_hours}
                />
              </StyledAppointmentContainer>
            )}
            <div style={{ display: hasDropoffAppointment ? 'inherit' : 'none' }}>
              {renderTimings(true)}
            </div>
            <div style={{ display: hasDropoffAppointment ? 'none' : 'inherit' }}>
              {renderTimings(false)}
            </div>
          </Flexer.Child>
        </StyledDiv>
      </Flexer.Parent>

      {showExtraInfo && (
        <Flexer.Parent mTop="M">
          <ExtraInfoSection
            locator={locator}
            name={name}
            stopPreffix={stopPreffix}
            disabledFields={disabledFields}
          />
        </Flexer.Parent>
      )}
      {allowRemove && (
        <Fragment>
          <RemoveStopButton onClick={onRemove} styledPosition={[0, 0]} />
          <ReorderStopButtons
            styledPosition={['50%', 0]}
            allowMoveUp={allowMoveUp}
            allowMoveDown={allowMoveDown}
            onUpClick={onMoveUp}
            onDownClick={onMoveDown}
          />
        </Fragment>
      )}
    </StyledShipmentBox>
  );
};

StopSection.propTypes = {
  stopPreffix: PropTypes.oneOf(['pickup', 'dropoff']),
  addressSuffix: PropTypes.oneOf(['from', 'to']),
  allowRemove: PropTypes.bool,
  onNewAddress: PropTypes.func,
  isMainStop: PropTypes.bool,
  showExtraInfo: PropTypes.bool,
  onRemove: PropTypes.func,
  shipperAddresses: PropTypes.array,
  userInfo: PropTypes.object.isRequired,
  onHandlingExtraChange: PropTypes.func,
  handlingExtraName: PropTypes.string.isRequired,
  handlingExtraChecked: PropTypes.bool,
  allowMoveUp: PropTypes.bool,
  allowMoveDown: PropTypes.bool,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  field: PropTypes.object,
  form: PropTypes.object,
  disabledFields: PropTypes.array.isRequired,
  onTimeChange: PropTypes.func,
  isRepeated: PropTypes.bool,
  isEdition: PropTypes.bool,
  shipment: PropTypes.object,
};

StopSection.defaultProps = {
  shipment: {},
  appointmentRequired: false,
  addressSuffix: 'from',
  allowRemove: false,
  onNewAddress: () => {
    /*noop*/
  },
  isMainStop: false,
  showExtraInfo: true,
  onRemove: () => {
    /*noop*/
  },
  shipperAddresses: [],
  onHandlingExtraChange: () => {
    /*noop*/
  },
  handlingExtraChecked: false,
  field: {},
  form: {},
  disabledFields: [],
  onTimeChange: undefined,
};

export default withFormikField(injectIntl(StopSection));
